/**
 * Chat Session Service
 * Manages chat sessions with SSE streaming, widget rendering, and status updates
 */

// Remove trailing slash if present to avoid double slashes
const API_URL = (process.env.REACT_APP_API_URL || "https://trowelwa01.azurewebsites.net/api").replace(/\/$/, '');

export interface WidgetSpec {
  widget_id: string;
  widget_type: string;
  spec: any;
  schema?: any;
  rendering_hints?: any;
  timestamp: string;
}

export type ContentSegment = 
  | { type: 'text'; content: string }
  | { type: 'widget'; widget: WidgetSpec };

export interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string; // Keep for backward compatibility and user messages
  contentSegments?: ContentSegment[]; // New: ordered segments for assistant messages
  timestamp: Date;
  isStreaming?: boolean;
  streamingStatus?: string; // Status message during streaming (e.g., "Thinking...", "Generating visualization...")
}

export interface SessionInfo {
  session_id: string;
  user_id: string;
  firm_id?: string;
  created_at: string;
  message_count: number;
  status: string;
}

export interface SSEEvent {
  type: 'status' | 'text' | 'widget' | 'completion' | 'error';
  message?: string;
  step?: string;
  content?: string;
  data?: any;
  metadata?: any;
  error?: string;
}

export class ChatSessionService {
  private sessionId: string | null = null;
  private eventSource: EventSource | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;

  constructor() {
    // Try to restore session from sessionStorage
    const storedSessionId = sessionStorage.getItem('chat_session_id');
    if (storedSessionId) {
      this.sessionId = storedSessionId;
    }
  }

  /**
   * Create a new chat session
   */
  async createSession(firmId?: string): Promise<SessionInfo> {
    const token = sessionStorage.getItem('access_token');
    
    const response = await fetch(`${API_URL}/trowel-chat/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify({
        firm_id: firmId,
        metadata: {
          platform: 'web',
          created_from: 'dashboard'
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to create session: ${response.statusText}`);
    }

    const session: SessionInfo = await response.json();
    this.sessionId = session.session_id;
    
    // Store in sessionStorage
    sessionStorage.setItem('chat_session_id', this.sessionId);
    
    return session;
  }

  /**
   * Get current session ID
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Get session details
   */
  async getSession(sessionId?: string): Promise<SessionInfo> {
    const id = sessionId || this.sessionId;
    if (!id) {
      throw new Error('No session ID available');
    }

    const token = sessionStorage.getItem('access_token');
    
    const response = await fetch(`${API_URL}/trowel-chat/sessions/${id}`, {
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get session: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get user's recent sessions
   */
  async getRecentSessions(limit: number = 10): Promise<SessionInfo[]> {
    const token = sessionStorage.getItem('access_token');
    
    const response = await fetch(
      `${API_URL}/trowel-chat/sessions?limit=${limit}`,
      {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get sessions: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Send message with SSE streaming
   */
  async sendMessage(
    message: string,
    handlers: {
      onStatus: (message: string, step: string) => void;
      onTextChunk: (text: string) => void;
      onWidget: (widget: WidgetSpec) => void;
      onComplete: (metadata: any) => void;
      onError: (error: string) => void;
    },
    firmId?: string
  ): Promise<void> {
    // Ensure we have a session
    if (!this.sessionId) {
      await this.createSession(firmId);
    }

    const token = sessionStorage.getItem('access_token');
    
    try {
      const response = await fetch(
        `${API_URL}/trowel-chat/sessions/${this.sessionId}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({
            message,
            firm_id: firmId
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle SSE stream
      await this._handleSSEStream(response, handlers);

    } catch (error) {
      console.error('Error sending message:', error);
      handlers.onError(error instanceof Error ? error.message : 'Unknown error');
      
      // Attempt to reconnect if connection failed
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        console.log(`Retrying... attempt ${this.reconnectAttempts}`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.sendMessage(message, handlers, firmId);
      }
    }
  }

  /**
   * Handle SSE stream
   */
  private async _handleSSEStream(
    response: Response,
    handlers: {
      onStatus: (message: string, step: string) => void;
      onTextChunk: (text: string) => void;
      onWidget: (widget: WidgetSpec) => void;
      onComplete: (metadata: any) => void;
      onError: (error: string) => void;
    }
  ): Promise<void> {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error('Response body is not readable');
    }

    let buffer = '';
    let currentEvent = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('Stream complete');
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const data = line.slice(6);
            try {
              const parsed: SSEEvent = JSON.parse(data);
              this._handleSSEEvent(currentEvent, parsed, handlers);
            } catch (e) {
              console.warn('Failed to parse SSE data:', data);
            }
          }
        }
      }

      // Reset reconnect attempts on successful completion
      this.reconnectAttempts = 0;

    } catch (error) {
      console.error('Stream reading error:', error);
      handlers.onError(error instanceof Error ? error.message : 'Stream error');
    }
  }

  /**
   * Handle individual SSE event
   */
  private _handleSSEEvent(
    eventType: string,
    event: SSEEvent,
    handlers: {
      onStatus: (message: string, step: string) => void;
      onTextChunk: (text: string) => void;
      onWidget: (widget: WidgetSpec) => void;
      onComplete: (metadata: any) => void;
      onError: (error: string) => void;
    }
  ): void {
    switch (eventType) {
      case 'status':
        if (event.message && event.step) {
          handlers.onStatus(event.message, event.step);
        }
        break;

      case 'text':
        if (event.content) {
          handlers.onTextChunk(event.content);
        }
        break;

      case 'widget':
        if (event.data) {
          handlers.onWidget(event.data as WidgetSpec);
        }
        break;

      case 'completion':
        if (event.metadata) {
          handlers.onComplete(event.metadata);
        }
        break;

      case 'error':
        handlers.onError(event.error || event.message || 'Unknown error');
        break;

      default:
        console.warn('Unknown SSE event type:', eventType, event);
    }
  }

  /**
   * Get conversation history
   */
  async getHistory(limit: number = 50): Promise<ChatMessage[]> {
    if (!this.sessionId) {
      return [];
    }

    const token = sessionStorage.getItem('access_token');
    
    const response = await fetch(
      `${API_URL}/trowel-chat/sessions/${this.sessionId}/history?limit=${limit}`,
      {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get history: ${response.statusText}`);
    }

    const messages = await response.json();
    
    return messages.map((msg: any) => ({
      id: msg.message_id,
      type: msg.role === 'user' ? 'user' : 'assistant',
      content: msg.content,
      widgets: msg.widgets,
      timestamp: new Date(msg.timestamp),
      isStreaming: false
    }));
  }

  /**
   * Delete current session
   */
  async deleteSession(): Promise<void> {
    if (!this.sessionId) {
      return;
    }

    const token = sessionStorage.getItem('access_token');
    
    await fetch(`${API_URL}/trowel-chat/sessions/${this.sessionId}`, {
      method: 'DELETE',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });

    // Clear session
    this.sessionId = null;
    sessionStorage.removeItem('chat_session_id');
  }

  /**
   * Close any open connections
   */
  closeConnection(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}
